import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { Country } from '../../country/Country';
import { CountryService } from '../../country/country.service';

import { MustMatch } from '../../_helpers/MustMatch';
import { ProductCategory } from '../../_models/product-category';
import { ProductCategoryService } from '../../_services/product-category.service';
import { UserService } from '../../_services/user.service';
import { PageTitleService } from '../../_services/page-title.service';
import { SectorService } from '../../_services/sector.service';
import { Title } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AppConstants } from 'src/app/_constants/constants';
import { IsPhoneNumberValid } from 'src/app/_helpers/IsPhoneNumberValid';
import { Sector } from 'src/app/_models/sector';

@Component({
    templateUrl: './signup.component.html',
    standalone: false
})
export class SignupComponent implements OnInit, OnDestroy {
    title = 'Create an account';
    titleTranslateKey = 'common.create_account';

    registerForm: UntypedFormGroup;
    loading = true;
    submitted = false;
    countries: Country[];
    productCategories: ProductCategory[];
    sectors: Sector[];
    usingFrench = true;

    /** Holds the currently selected Country object so we can display its flag */
    selectedCountry: Country | null = null;

    subscriptions: Subscription[] = [];

    selectedUserType = 'buyer';

    constructor(
        private formBuilder: UntypedFormBuilder,
        private userService: UserService,
        private titleService: PageTitleService,
        private router: Router,
        private route: ActivatedRoute,
        private toastr: ToastrService,
        private cs: CountryService,
        private pcs: ProductCategoryService,
        private sectorService: SectorService,
        private titleS: Title,
        private translateService: TranslateService
    ) {}

    ngOnInit() {
        this.usingFrench = this.translateService.currentLang === AppConstants.FRENCH;

        const pageTitleSub = this.translateService.get(this.titleTranslateKey).subscribe(value => {
            this.title = value;
            this.titleService.setPageTitle(this.title);
        });
        this.subscriptions.push(pageTitleSub);

        const titleSub = this.translateService.get('rightnav.sign_up').subscribe(value => {
            this.titleS.setTitle(value);
        });
        this.subscriptions.push(titleSub);

        this.route.queryParams.subscribe(params => {
            const userType = params['userType'];
            if (userType && ['buyer', 'seller', 'carrier'].includes(userType)) {
                this.selectedUserType = userType;
            }
        });

        this.getSectors();
        this.setCountries();
        this.setProductCategoryIds();
    }

    buildForm() {
        const passwordValidator = [
            Validators.minLength(8),
            Validators.required,
            Validators.pattern(/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/)
        ];

        this.registerForm = this.formBuilder.group(
            {
                userType: new UntypedFormControl(this.selectedUserType, Validators.required),
                firstName: new UntypedFormControl('', Validators.required),
                lastName: new UntypedFormControl('', Validators.required),
                email: new UntypedFormControl('', [Validators.required, Validators.email]),
                code: new UntypedFormControl(37, Validators.required),
                phone: new UntypedFormControl('', [Validators.required]),
                password: new UntypedFormControl('', passwordValidator),
                confirmPassword: new UntypedFormControl('')
            },
            {
                validator: [
                    MustMatch('password', 'confirmPassword'),
                    IsPhoneNumberValid('code', 'phone')
                ]
            }
        );

        // Set the initial selected country to match the default code (37)
        this.updateSelectedCountry(37);

        // Keep selectedCountry in sync when the code control changes programmatically
        this.registerForm.get('code').valueChanges.subscribe(id => {
            this.updateSelectedCountry(id);
        });
    }

    /**
     * Called by the mat-select (selectionChange) event in the template.
     * The value is already set on the form control; we just sync the flag display.
     */
    onCountryChange(event: any) {
        const id = event.value;
        this.updateSelectedCountry(id);
    }

    private updateSelectedCountry(id: number) {
        this.selectedCountry = this.countries?.find(c => c.id === +id) ?? null;
    }

    setCountries() {
        this.cs.loadAll().subscribe(data => {
            this.countries = data;
            localStorage.setItem('countries', JSON.stringify(this.countries));
            this.buildForm();
            this.loading = false;
        });
    }

    setProductCategoryIds() {
        this.pcs.get_product_categories().subscribe(data => {
            this.productCategories = data;
        });
    }

    getSectors() {
        this.sectorService.getAll().subscribe((sectors: any) => {
            this.sectors = sectors;
        });
    }

    // Convenience getter for easy access to form fields
    get f() { return this.registerForm.controls; }

    onSubmit() {
        this.submitted = true;

        if (!this.registerForm.valid) {
            this.submitted = false;
            return;
        }

        // Convert code to string as backend expects string value
        const formData = {
            ...this.registerForm.value,
            code: String(this.registerForm.value.code)
        };

        this.userService.register(formData).subscribe(
            (response) => {
                this.setSuccessMessage();
                this.router.navigateByUrl('signup/confirmation');
            },
            (error: any) => {
                this.loading = false;
                this.submitted = false;
                this.setErrorMessage(error);
            }
        );
    }

    setSuccessMessage() {
        const e = document.getElementById('success-messages');

        const successSub = this.translateService.get('common.successfully_done').subscribe(value => {
            if (e) { e.innerHTML = value; }
        });
        this.subscriptions.push(successSub);

        const toastrSub = this.translateService.get('login.success_message').subscribe(value => {
            this.toastr.success(value);
        });
        this.subscriptions.push(toastrSub);
    }

    setErrorMessage(error: any) {
        const e = document.getElementById('error-messages');

        // Extract a clean string message from the HttpErrorResponse
        let displayMessage: string;
        const fieldErrors = error?.error?.errors;

        if (fieldErrors && typeof fieldErrors === 'object') {
            // Join all field-level errors into one readable string
            displayMessage = Object.values(fieldErrors).join(', ');
        } else if (error?.error?.message) {
            displayMessage = error.error.message;
        } else if (typeof error?.error === 'string') {
            displayMessage = error.error;
        } else {
            displayMessage = 'An error occurred. Please try again.';
        }

        if (e) { e.innerHTML = displayMessage; }

        setTimeout(() => {
            if (e) { e.innerHTML = ''; }
        }, 5000);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}